/**
 * Wire shapes and German vocabulary for the register-credential screen (#469, step 7).
 *
 * A credential is the reusable pair a till signs in with: a **Kassen-Kennung** (`cqme-` + 24 hex, typed into
 * the register's *username* field) and a **Passwort** (typed into its *password* field). The backend stores
 * only a PBKDF2 verifier of the password, so `DeviceCredentialSecret` — returned by issue and by rotate, and
 * by nothing else — is the only shape in this app that ever carries the plaintext. Nothing here writes it
 * anywhere: not to a store, not to `localStorage` (which `CookieService` traps anyway), not to a log line.
 */
import {readErrorCode} from '../../../_auth/two-factor/second-factor-errors';

/**
 * A credential as listed. Mirrors `API.Core.Models.Device.DeviceCredentialSummary`, which drops the verifier
 * on the way out — there is no field here that could carry a secret, by construction.
 */
export interface DeviceCredentialSummary {
  id: string;
  /** What is typed into the register's *username* field. Not secret; it is printed on iPads. */
  clientId: string;
  /** The administrator's own label ("Tresen", "Terrasse"). Never part of authentication. */
  name: string;
  shardId: string;
  /**
   * Historical only (#279). A credential names no account any more — a register presenting it receives a
   * `[Register]`-only token scoped to `shardId` — so this is `null` for everything issued since, and carries the
   * old anchor for rows issued before. Nothing on the screen reads it.
   */
  userId: string | null;
  /** ISO-8601 UTC. */
  issuedAt: string;
  /** ISO-8601 UTC, or null while the password is still the original one. */
  rotatedAt: string | null;
  /** ISO-8601 UTC — when this password stops being able to acquire a token. */
  expiration: string;
  /** ISO-8601 UTC, or null while live. */
  revokedAt: string | null;
  /** ISO-8601 UTC of the last successful device sign-in, or null. */
  lastUsedAt: string | null;
  expired: boolean;
  revoked: boolean;
}

/**
 * A credential as returned by **issue** and **rotate** — the only two responses that carry a plaintext
 * password, and the only moment it exists outside the till it is typed into.
 */
export interface DeviceCredentialSecret {
  id: string;
  clientId: string;
  /** Shown once. There is no read that can return it again. */
  secret: string;
  expiration: string;
}

/** The company-wide rotation interval, in days. Mirrors `DeviceCredentialRotationSetting`. */
export interface DeviceCredentialRotation {
  days: number;
}

/** Shortest interval the server accepts. Mirrors `DeviceCredential.IsRotationInRange`. */
export const ROTATION_MIN_DAYS = 1;

/** Longest interval the server accepts — one year. Mirrors `DeviceCredential.MaximumRotationDays`. */
export const ROTATION_MAX_DAYS = 365;

/** Interval a company that has never chosen one gets. Mirrors `DeviceCredential.DefaultRotationDays`. */
export const ROTATION_DEFAULT_DAYS = 90;

/**
 * The three states a credential can be in on this screen.
 *
 * Derived from the server's own `expired`/`revoked` booleans rather than by comparing `expiration` against
 * the browser's clock: the two clocks are not the same clock, and a row shown as live when the server calls
 * it expired would send an installer to a till with a password that cannot work.
 */
export type DeviceCredentialState = 'live' | 'expired' | 'revoked';

/**
 * Which state a row is in. **Revoked outranks expired**, because the remedies differ: an expired credential
 * is repaired by rotating it, a revoked one only by issuing a new one — and a revoked credential that also
 * lapsed must not offer the rotate remedy the server would refuse.
 */
export function credentialState(credential: DeviceCredentialSummary): DeviceCredentialState {
  if (credential.revoked) return 'revoked';
  return credential.expired ? 'expired' : 'live';
}

/** The German word for each state, as it appears in the status badge. */
export const CREDENTIAL_STATE_LABEL: Record<DeviceCredentialState, string> = {
  live: 'Aktiv',
  expired: 'Abgelaufen',
  revoked: 'Gesperrt'
};

/**
 * The one sentence stating the permitted rotation range.
 *
 * Exported so this screen's own pre-flight check and the server's
 * `E_DEVICE_CREDENTIAL_ROTATION_OUT_OF_RANGE` cannot word the same rule differently — a field that refuses
 * an empty value locally and a server that refuses 3650 must not read as two different rules.
 */
export const ROTATION_RANGE_MESSAGE =
  'Das Intervall muss zwischen ' + ROTATION_MIN_DAYS + ' und ' + ROTATION_MAX_DAYS + ' Tagen liegen.';

/**
 * The German sentence for a structured refusal, keyed on `ApiError.code`.
 *
 * Per the style guide the backend's English `description` is developer-facing and is never rendered — so
 * every code these endpoints can answer gets an explicit sentence here, and anything unrecognised falls back
 * to the caller's own fallback rather than to the raw field.
 */
const CREDENTIAL_ERRORS: Record<string, string> = {
  // Only reachable from a device sign-in, never from this screen. Mapped anyway: the vocabulary is one table
  // per feature, and a code that exists but is missing from the table is how English reaches a screen later.
  E_DEVICE_CREDENTIAL_INVALID:
    'Kennung oder Passwort dieser Kasse gelten nicht. Stellen Sie neue Zugangsdaten aus.',
  E_DEVICE_CREDENTIAL_EXPIRED:
    'Das Passwort dieser Zugangsdaten ist abgelaufen. Wechseln Sie das Passwort.',
  E_DEVICE_CREDENTIAL_REVOKED:
    'Diese Zugangsdaten sind gesperrt. Stellen Sie neue Zugangsdaten aus.',
  E_DEVICE_CREDENTIAL_NOT_A_DEVICE_LOGIN:
    'Diese Zugangsdaten gelten nur für Kassen. Melden Sie sich damit nicht in der Verwaltung an.',
  // Its own sentence, not a variant of "gesperrt": the remedy is a NEW credential, and rotating is exactly
  // what the reader just tried. Saying only "gesperrt" sends them back to the same button.
  E_DEVICE_CREDENTIAL_ALREADY_REVOKED:
    'Diese Zugangsdaten sind bereits gesperrt. Ein Passwortwechsel ist nicht mehr möglich. '
    + 'Stellen Sie neue Zugangsdaten aus.',
  E_DEVICE_CREDENTIAL_NOT_FOUND:
    'Diese Zugangsdaten gibt es nicht mehr. Laden Sie die Seite neu.',
  // "Standort", not "Verkaufspunkt": a credential is scoped to a shard. A Verkaufspunkt is a location INSIDE
  // a shard (the device cards use the word for that), and naming the shard by it sent readers there.
  E_DEVICE_CREDENTIAL_FORBIDDEN_SHARD:
    'Sie dürfen Zugangsdaten nur für Ihren eigenen Standort verwalten.',
  // The residual tenant refusal (#279): a credential binds to a company + shard and names no account, so this
  // is the server's answer when that pair cannot be resolved. There is no account to choose differently, which
  // is why the remedy is a fresh session. (`E_DEVICE_CREDENTIAL_ACCOUNT_NOT_REGISTER_ONLY` is retired with the
  // anchor it gated — a `[Register]`-only token cannot inherit anything.)
  E_DEVICE_CREDENTIAL_USER_NOT_ELIGIBLE:
    'Für diesen Standort können derzeit keine Zugangsdaten ausgestellt werden. '
    + 'Bitte melden Sie sich erneut an und versuchen Sie es noch einmal.',
  E_DEVICE_CREDENTIAL_ROTATION_OUT_OF_RANGE: ROTATION_RANGE_MESSAGE
};

/** Shown for a refusal this build has no sentence for, so no English ever reaches the screen. */
export const CREDENTIAL_ERROR_FALLBACK =
  'Die Aktion war nicht möglich. Bitte versuchen Sie es erneut.';

/**
 * The German sentence for a failed credential call.
 *
 * @param err      whatever rxjs handed the `error` callback.
 * @param fallback the sentence for an unrecognised failure. Override it per call site — several of these
 *                 endpoints answer a bare status with no `code` at all (a `[Authorize]` refusal, a plain
 *                 `NotFound()`), so the fallback is the whole message there, not a decoration.
 */
export function describeCredentialError(err: unknown,
                                        fallback: string = CREDENTIAL_ERROR_FALLBACK): string {
  const code = readErrorCode(err);
  return (code && CREDENTIAL_ERRORS[code]) || fallback;
}
